import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EventsPage from './pages/EventsPage';
import EventDetailsPage from './pages/EventDetailsPage';
import LoginPage from './pages/LoginPage';
import AdminPanel from './pages/AdminPanel';
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookieConsent from 'react-cookie-consent';
import { getUserRole } from './utils/jwtHelper';
import StorageManager from './utils/StorageManager';
import './i18n/i18n';
import { API_BASE_URL, isFeatureEnabled } from './config/environment';

// Komponent sprawdzający uprawnienia
const ProtectedRoute = ({ element: Element, allowedRoles }) => {
  const token = localStorage.getItem('token');
  const userRole = getUserRole();

  if (!token) {
    return <Navigate 
      to="/login"
      replace 
    />;
  }

  if (!allowedRoles.includes(userRole)) {
    localStorage.removeItem('token');
    localStorage.removeItem('username');

    return <Navigate 
      to="/"
      replace 
      state={{ 
        authError: 'Brak uprawnień do tej strony.'
      }} 
    />;
  }

  return <Element />;
};

// Komponent przekierowujący do zewnętrznego menu
const MenuRedirect = () => {
  useEffect(() => {
    window.location.href = 'https://restauracjatortuga.goorder.pl/';
  }, []);
  return null;
};

// Komponent główny aplikacji
const AppContent = () => {
  const location = useLocation();
  const isLoggedIn = localStorage.getItem('token');
  const userRole = localStorage.getItem('role');
  const features = {
    events: isFeatureEnabled('events'),
    reservations: isFeatureEnabled('reservations'),
  };

  useEffect(() => {
    if (location.state?.authError) {
      toast.error(location.state.authError, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      window.history.replaceState({}, document.title);
    }

    // Obsługa komunikatów rezerwacji
    if (location.state?.reservationMessage) {
      toast.success(location.state.reservationMessage, {
        position: "top-center",
        autoClose: 5000
      });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      return;
    }

    const abortSupported = typeof AbortController !== 'undefined';
    const controller = abortSupported ? new AbortController() : null;
    const trackVisit = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json'
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        await fetch(`${API_BASE_URL}/api/track-visit`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ page: `${location.pathname}${location.search}` }),
          signal: controller?.signal,
          keepalive: true
        });
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        if (process.env.NODE_ENV === 'development') {
          console.error('Visit tracking failed:', error);
        }
      }
    };

    trackVisit();

    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, [location.pathname, location.search]);

  return (
    <>
      {!['/login', '/admin'].includes(location.pathname) && (
        <Navbar isLoggedIn={isLoggedIn} userRole={userRole} />
      )}
      
      <Routes>
        {/* Publiczne ścieżki */}
        <Route path="/" element={<HomePage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/:slug" element={<EventDetailsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/polityka-prywatnosci" element={<PrivacyPolicy />} />
        <Route path="/menu" element={<MenuRedirect />} />

        {/* Panel administracyjny - dostępny dla admin i scanner */}
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute 
              element={AdminPanel} 
              allowedRoles={['admin', 'scanner']} 
            />
          } 
        />

        {/* Przekierowanie na stronę główną dla nieistniejących ścieżek */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

// Wrapper dla całej aplikacji
const App = () => {
  return (
    <Router>
      <CookieConsent
        location="bottom"
        buttonText="Rozumiem"
        cookieName="cookieConsent"
        style={{ 
          background: "#2B373B",
          zIndex: 9999
        }}
        buttonStyle={{ 
          color: "white", 
          backgroundColor: "#E50914", 
          fontSize: "14px",
          borderRadius: "4px",
          padding: "10px 20px",
          margin: "10px"
        }}
        expires={365}
      >
        <span style={{ fontSize: "14px" }}>
          Ta strona używa wyłącznie niezbędnych plików cookie do prawidłowego działania. 
          Więcej informacji znajdziesz w{" "}
          <Link 
            to="/polityka-prywatnosci" 
            style={{
              color: "white", 
              textDecoration: "underline"
            }}
          >
            Polityce Prywatności
          </Link>
          .
        </span>
      </CookieConsent>
      <ToastContainer />
      <AppContent />
    </Router>
  );
};

export default App;